























import {Component, Vue, Prop, Watch} from 'vue-property-decorator';
import {InputVariableViewModel, StateViewModel} from '@/features/bots/models/designer';
import { mixin as clickaway } from 'vue-clickaway';
import ActionBar from '@/features/bots/components/designer/TextEditor/ActionBar.vue';
import VariableBadge from './VariableBadge.vue';
import Toast from '@/shared/services/toast';
import shortid from 'shortid';
import HtmlFormatter from '@/shared/services/htmlFormatter';
import { Getter } from 'vuex-class';
import { ContactAttributeViewModel } from '@/messaging/models/ContactAttributeViewModel';
import { ContactAttributeType } from '@/messaging/models/contactAttributeType';

@Component({
	components: {
		ActionBar
	},
	mixins: [clickaway],
})
export default class TextEditor extends Vue {
	@Prop() public value: string;
	@Prop() public placeholder: string;
	@Prop({default: true}) public showVariablePicker: boolean;
	@Getter public getAttribute!: (attributeId: string) => ContactAttributeViewModel | undefined;
	@Getter public contactAttributes!: ContactAttributeViewModel[];

	public variableIds: string[] = [];
	public selection: any = null;

	public text: string = '';
	public isEditing: boolean = false;
	public editableTextId: string = '';

	public idAndVariable = new Map<string, InputVariableViewModel>();
	private editableDiv: HTMLDivElement;
	private containsInvalidAttribute: boolean;

	@Watch('contactAttributes', { immediate: true })
	public onAttributesChanged(value: StateViewModel, previous: StateViewModel) {
		this.reevaluateBadgeComponent();
	}

	public beforeMount() {
		this.editableTextId = shortid.generate();
	}

	public mounted() {
		this.editableDiv = this.$refs[this.editableTextId] as HTMLDivElement;
		this.setupEditableDivEvents();
		let newValue = this.value;
		this.editableDiv.focus();
		this.restoreSelection();
		newValue = HtmlFormatter.escapeHtmlCharacters(newValue);
		newValue = HtmlFormatter.convertNewLinesToBreaks(newValue);

		if (this.showVariablePicker && this.containsVariable()) {
			newValue = this.convertVariablesToHtml(newValue);
			HtmlFormatter.pasteHtmlAtCaret(newValue);
			for (const id of this.variableIds) {
				this.insertVariableBadgeElement(id);
			}
		} else {
			HtmlFormatter.pasteHtmlAtCaret(newValue);
		}

		HtmlFormatter.setCaretAtTheEndOfContentEditable(this.editableDiv);

		// blur the element if it already has a value
		if (this.value.length && this.value.length > 0) {
			this.blurElement();
		}
		this.$emit('error', this.containsInvalidAttribute);
	}

	public hasText() {
		return this.text && this.text.length > 0;
	}

	public startEditing() {
		this.isEditing = true;
		this.focusElement();
	}

	public finishEditing(event: any) {
		if (!this.isEditing) {
			return;
		}

		if (this.hasClickedTooltip(event)) {
			return;
		}
		this.isEditing = false;
		this.editableDiv.blur();

		this.convertToProcessableText();
		this.$emit('error', this.containsInvalidAttribute);
	}

	public addVariable(variable: InputVariableViewModel) {
		this.editableDiv.focus();
		this.restoreSelection();
		const id = `pill-${shortid.generate()}`;
		this.variableIds.push(id);
		const inputAsHtml = `<div id="${id}"></div>`;

		HtmlFormatter.pasteHtmlAtCaret(inputAsHtml);

		this.createVariableBadgeComponent(id, variable);
		HtmlFormatter.setCaretAfterElement(id);
	}

	public addEmoji(emoji: any) {
		this.editableDiv.focus();
		this.restoreSelection();
		HtmlFormatter.pasteHtmlAtCaret(emoji);
	}

	public getPlaceholder() {
		return this.placeholder ? this.placeholder : '&nbsp;';
	}

	public saveSelection() {
		this.isEditing = true;
		this.selection = HtmlFormatter.getSelection();
	}

	public focusElement() {
		this.editableDiv.focus();
		if (this.editableDiv.innerText?.length === 0) {
			HtmlFormatter.setCaretAtTheEndOfContentEditable(this.editableDiv);
		}
	}

	private blurElement() {
		this.editableDiv.blur();
	}

	private insertVariableBadgeElement(id: string) {
		const variable = this.idAndVariable.get(id);
		if (variable) {
			this.createVariableBadgeComponent(id, variable);
		}
		HtmlFormatter.setCaretAtTheEndOfContentEditable(this.editableDiv);
	}

	private containsVariable() {
		return this.value.includes('{{');
	}

	private hasClickedTooltip(event: any) {
		return event && event.toElement
			&& event.toElement.className
			&& typeof(event.toElement.className) === 'string'
			&& (event.toElement.className.startsWith('tooltip') || event.toElement.className.startsWith('fallback-text'));
	}

	private setupEditableDivEvents() {

		if (this.editableDiv) {
			this.editableDiv.addEventListener('paste', (e: ClipboardEvent) => {
				e.preventDefault();
				if (e && e.clipboardData) {
					let text = e.clipboardData.getData('text/plain');
					text = HtmlFormatter.escapeHtmlCharacters(text);
					text = HtmlFormatter.convertNewLinesToBreaks(text);
					document.execCommand('insertHTML', false, text);
				}
			});

			this.editableDiv.addEventListener('keydown', (e: KeyboardEvent) => {
				const bannedCommands = 'iIbBuU';
				if (e.ctrlKey && bannedCommands.includes(e.key)) {
					e.preventDefault();
				}
			});
		}
	}

	private restoreSelection() {
		if (this.selection) {
			HtmlFormatter.setSelection(this.selection);
		}
	}

	private convertVariablesToHtml(newValue: string) {
		const regexForVariable = /{{.*?.}}/;
		while (regexForVariable.exec(newValue)) {
			const variableRegArray = regexForVariable.exec(newValue);
			if (variableRegArray && variableRegArray[0]) {
				newValue = this.convertVariableToHtml(variableRegArray, newValue);
			}
		}
		return newValue;
	}

	private createVariableBadgeComponent(id: string, variable: InputVariableViewModel) {
		const vmComponent = new Vue({
			render: (h) => h(VariableBadge, {
				props: {
					id,
					variable,
					fallback: variable.fallback ? variable.fallback : ''
				},
				on: {
					closedFallback: this.finishEditing
				}}),
		});
		const mountedInstance = vmComponent.$mount(`#${id}`);
		variable.id = id;
		this.$refs[id] = (mountedInstance as any)._vnode.componentInstance;
	}

	private convertToProcessableText() {
		if (this.editableDiv) {
			let html = this.editableDiv.innerHTML;
			html = this.convertHtmlElementsToVariables(html);
			html = HtmlFormatter.cleanseHtml(html);
			html = HtmlFormatter.convertEscapedHtmlToString(html);
			this.$emit('text-updated', html);
		}
	}

	private convertHtmlElementsToVariables(html: string) {
		this.containsInvalidAttribute = false;
		for (const id of this.variableIds) {
			const currentVariable = document.getElementById(id) as HTMLElement;
			if (currentVariable) {
				html = this.convertHtmlElementToVariable(currentVariable, html);
			}
		}
		return html;
	}

	private convertHtmlElementToVariable(currentVariable: HTMLElement, html: string): string {
		const pillElements  = currentVariable.getElementsByClassName('pill');
		if (this.pillsContainInvalidAttribute(pillElements)) {
			this.containsInvalidAttribute = true;
		}
		if (pillElements.length > 0 && pillElements[0]) {
			const elementWithData = pillElements[0];
			const fallback = elementWithData.getAttribute('data-fallback');
			const scope = elementWithData.getAttribute('data-scope');
			const key = elementWithData.getAttribute('data-key');
			const elementAsHtml = currentVariable.outerHTML;
			let scopePrefix = '';
			if (scope === 'appUser' || scope === 'appUser.customAttributes') {
				scopePrefix = `${scope}.`;
			}
			html = this.replaceHtmlWithVariable(fallback, html, elementAsHtml, scopePrefix, key);
		}
		return html;
	}

	private pillsContainInvalidAttribute(pillElements: HTMLCollectionOf<Element>) {
		for (const pillElement of pillElements) {
			if (this.pillIsInvalid(pillElement)) {
				return true;
			}
		}
		return false;
	}

	private replaceHtmlWithVariable(fallback: string | null, html: string, elementAsHtml: string, scopePrefix: string, key: string | null): string {
		if (!key) {
			Toast.showError('There was an error');
		}
		if (fallback) {
			html = html.replace(elementAsHtml, `{{${scopePrefix}${key}|'${fallback}'}}`);
		} else {
			html = html.replace(elementAsHtml, `{{${scopePrefix}${key}|''}}`);
		}
		return html;
	}

	private convertVariableToHtml(variableRegArray: any[], newValue: string) {
		const regexForVariable = /{{.*?.}}/;
		const variableWithBracket = variableRegArray[0];
		const variableWithoutBracket = variableWithBracket.substring(2, variableWithBracket.length - 2);

		const variableData = variableWithoutBracket.split('|');
		const variableSyntax = variableData[0];
		const fallback = variableData[1];

		const variable = this.extractVariableAndScope(variableSyntax);

		variable.fallback = fallback.substring(1, fallback.length - 1);
		const id = `pill-${shortid.generate()}`;
		this.variableIds.push(id);

		this.idAndVariable.set(id, variable);

		const inputAsHtml = `<div id="${id}"></div>`;

		newValue = newValue.replace(regexForVariable, inputAsHtml);

		this.editableDiv.focus();
		return newValue;
	}

	private extractVariableAndScope(variableSyntax: string): InputVariableViewModel {
		const syntaxParts = variableSyntax.split('.');

		if (syntaxParts[2]) {
			return this.convertCustomAttributeVariable(syntaxParts);
		} else if (syntaxParts[1]) {
			return this.convertSystemVariable(syntaxParts);
		} else {
			return {
				id: '',
				key: syntaxParts[0].trim(),
				name: syntaxParts[0].trim(),
				scope: 'flow'
			} as InputVariableViewModel;
		}
	}

	private convertCustomAttributeVariable(value: string[]): InputVariableViewModel {
		let dataType = ContactAttributeType.Text;
		const variableScope = `${value[0]}.${value[1]}`;
		const variableKey = value[2].trim();
		let variableName = variableKey;
		let error = '';
		const contactAttribute = this.getAttribute(variableKey);
		if (contactAttribute) {
			dataType = contactAttribute.type;
			variableName = contactAttribute.name;

			if (contactAttribute.archived) {
				error = 'Attribute has been archived';
				this.containsInvalidAttribute = true;
			}
		} else {
			error = 'Attribute doesn\'t exist';
			this.containsInvalidAttribute = true;
		}
		return {
			id: '',
			key : variableKey,
			name: variableName,
			scope: variableScope,
			dataType,
			error
		} as InputVariableViewModel;
	}

	private convertSystemVariable(value: string[]): InputVariableViewModel {
		const variableScope = value[0].trim();
		const variableKey = value[1].trim();
		let variableName = variableKey;
		let dataType = ContactAttributeType.Text;
		const contactAttribute = this.getAttribute(variableKey);
		if (contactAttribute) {
			variableName = contactAttribute.name;
			dataType = contactAttribute.type;
		}
		if (variableKey === 'surname') {
			variableName = 'Last Name';
		} else if (variableKey === 'givenName') {
			variableName = 'First Name';
		}
		return {
			id : '',
			key : variableKey,
			name: variableName,
			scope: variableScope
		} as InputVariableViewModel;
	}

	private reevaluateBadgeComponent() {
		this.idAndVariable.forEach((variable) => {
			if (variable.error) {
				this.reevaluateInvalidBadgeComponent(variable);
			}
		});
	}

	private reevaluateInvalidBadgeComponent(variable: InputVariableViewModel) {
		const contactAttribute = this.getAttribute(variable.key);
		if (contactAttribute) { // Previously missing attribute now exists, clear error
			variable.error = '';
			variable.dataType = contactAttribute.type;
			variable.name = contactAttribute.name;

			const badge = this.$refs[variable.id] as VariableBadge;
			badge.update();
		}
	}

	private pillIsInvalid(pillElement: Element) {
		const key = pillElement.getAttribute('data-key');
		const scope = pillElement.getAttribute('data-scope');
		if (key && scope === 'appUser.customAttributes') {
			const attribute = this.getAttribute(key);
			if (attribute && attribute.archived || !attribute) {
				return true;
			}
		}
		return false;
	}
}
